import React, { useMemo, useState } from 'react';
import FamilyDialog from './FamilyDialog';
import DateOfBirthSelect from '../../ui/DateOfBirthSelect';
import { Gender, ShirtSize } from '../../../types';
import { toTitleCase } from '../../../utils/textNormalization';
import { familyPriceCents, familyChangeIncreaseCents, FAMILY_MAX_SIZE } from '@shared/fees';
import { householdFromMembers } from '@shared/familyLimits';
import { clubYear } from '@shared/membership';
import type { FamilyMemberSummary } from './types';
import { callManageFamily } from './manageFamilyClient';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AddFamilyMemberDialogProps {
  /** The family's familyGroupId, or null to start a new family with the primary as the sole adult. */
  familyGroupId: string | null;
  /** Every active member currently in the family (used for the before/after preview). */
  currentFamily: FamilyMemberSummary[];
  onClose: () => void;
  onDone: (result: { message: string; changedMemberIds: string[] }) => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: Gender;
  shirtSize: ShirtSize;
  email: string;
  phone: string;
  wantsPortalAccess: boolean;
  paidAtDesk: boolean;
  adminOverrideReason: string;
}

const AddFamilyMemberDialog: React.FC<AddFamilyMemberDialogProps> = ({
  familyGroupId,
  currentFamily,
  onClose,
  onDone,
}) => {
  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: Gender.PREFER_NOT_TO_SAY,
    shirtSize: ShirtSize.M,
    email: '',
    phone: '',
    wantsPortalAccess: false,
    paidAtDesk: false,
    adminOverrideReason: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const year = clubYear();

  const before = useMemo(() => householdFromMembers(currentFamily, year), [currentFamily, year]);
  const after = useMemo(() => {
    if (!form.dateOfBirth) return before;
    return householdFromMembers([...currentFamily, { dateOfBirth: form.dateOfBirth }], year);
  }, [currentFamily, form.dateOfBirth, year]);

  const willExceedTen = before.adults + before.juniors + 1 > FAMILY_MAX_SIZE;
  const owedCents = form.dateOfBirth ? familyChangeIncreaseCents(before, after) : null;
  const dollars = (cents: number) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
  const preview =
    owedCents == null
      ? null
      : `Adding a ${after.juniors > before.juniors ? 'junior' : 'family member'}: ${dollars(
          familyPriceCents(before)
        )} → ${dollars(familyPriceCents(after))}, ${owedCents === 0 ? 'nothing owed' : `${dollars(owedCents)} owed`}`;

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = 'First name is required';
    if (!form.lastName.trim()) next.lastName = 'Last name is required';
    if (!form.dateOfBirth) next.dateOfBirth = 'Date of birth is required';
    if (form.wantsPortalAccess) {
      if (!form.email.trim()) next.email = 'Email is required for a login';
      else if (!EMAIL_REGEX.test(form.email.trim())) next.email = 'Please enter a valid email address';
    } else if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) {
      next.email = 'Please enter a valid email address';
    }
    if (willExceedTen && !form.adminOverrideReason.trim()) {
      next.adminOverrideReason = 'A reason is required when the family goes over 10 people';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const payload: Record<string, unknown> = {
        familyGroupId,
        person: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          dateOfBirth: form.dateOfBirth,
          gender: form.gender,
          shirtSize: form.shirtSize,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          wantsPortalAccess: form.wantsPortalAccess,
        },
        paidAtDesk: form.paidAtDesk,
      };
      if (form.adminOverrideReason.trim()) payload.adminOverrideReason = form.adminOverrideReason.trim();

      const result = await callManageFamily('ADD_PERSON', payload);
      if (result.success) {
        onDone({
          message: result.message || `${form.firstName} ${form.lastName} was added to the family.`,
          changedMemberIds: result.changedMemberIds || [],
        });
      } else {
        setServerError(result.message || 'Could not add the family member. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not add the family member. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FamilyDialog
      titleId="add-family-member-title"
      title="Add family member"
      descriptionId="add-family-member-desc"
      busy={submitting}
      onClose={onClose}
      widthClassName="max-w-lg"
    >
      <p id="add-family-member-desc" className="mt-1 text-sm text-gray-600">
        Enter the new person&apos;s details. Their age category is worked out from their date of
        birth.
      </p>

      {serverError && (
        <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
          {serverError}
        </p>
      )}

      <div className="mt-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="afm-first-name" className="block text-sm font-medium text-gray-700 mb-1">
              First name *
            </label>
            <input
              id="afm-first-name"
              type="text"
              value={form.firstName}
              onChange={(e) => setForm((p) => ({ ...p, firstName: toTitleCase(e.target.value) }))}
              className={`min-h-[44px] w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="afm-last-name" className="block text-sm font-medium text-gray-700 mb-1">
              Last name *
            </label>
            <input
              id="afm-last-name"
              type="text"
              value={form.lastName}
              onChange={(e) => setForm((p) => ({ ...p, lastName: toTitleCase(e.target.value) }))}
              className={`min-h-[44px] w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of birth *</label>
          <DateOfBirthSelect
            value={form.dateOfBirth}
            onChange={(v) => setForm((p) => ({ ...p, dateOfBirth: v }))}
            error={!!errors.dateOfBirth}
            idPrefix="afm-dob"
          />
          {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="afm-gender" className="block text-sm font-medium text-gray-700 mb-1">
              Gender
            </label>
            <select
              id="afm-gender"
              value={form.gender}
              onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value as Gender }))}
              className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            >
              <option value={Gender.MALE}>Male</option>
              <option value={Gender.FEMALE}>Female</option>
              <option value={Gender.OTHER}>Other</option>
              <option value={Gender.PREFER_NOT_TO_SAY}>Prefer not to say</option>
            </select>
          </div>
          <div>
            <label htmlFor="afm-shirt" className="block text-sm font-medium text-gray-700 mb-1">
              Shirt size
            </label>
            <select
              id="afm-shirt"
              value={form.shirtSize}
              onChange={(e) => setForm((p) => ({ ...p, shirtSize: e.target.value as ShirtSize }))}
              className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            >
              {Object.values(ShirtSize).map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="afm-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email (optional)
            </label>
            <input
              id="afm-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              className={`min-h-[44px] w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>
          <div>
            <label htmlFor="afm-phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone (optional)
            </label>
            <input
              id="afm-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            />
          </div>
        </div>

        <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.wantsPortalAccess}
            onChange={(e) => setForm((p) => ({ ...p, wantsPortalAccess: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
          />
          Wants a login
        </label>

        {preview && (
          <p aria-live="polite" className="rounded-lg border border-fishing-green/30 bg-fishing-green/10 p-3 text-sm font-medium text-fishing-green">
            {preview}
          </p>
        )}

        <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.paidAtDesk}
            onChange={(e) => setForm((p) => ({ ...p, paidAtDesk: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
          />
          Paid at the desk
        </label>

        {willExceedTen && (
          <div>
            <label htmlFor="afm-override-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason for going over 10 people *
            </label>
            <input
              id="afm-override-reason"
              type="text"
              value={form.adminOverrideReason}
              onChange={(e) => setForm((p) => ({ ...p, adminOverrideReason: e.target.value }))}
              className={`min-h-[44px] w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue ${errors.adminOverrideReason ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.adminOverrideReason && (
              <p className="mt-1 text-sm text-red-600">{errors.adminOverrideReason}</p>
            )}
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Adding…' : 'Add family member'}
        </button>
      </div>
    </FamilyDialog>
  );
};

export default AddFamilyMemberDialog;
